// ** Modules
import Image, { type ImageProps } from 'next/image';

// ** UI
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Utils
import { getStrapiImageUrl } from '@/utils/api/strapi';

// ** Types
import type { Theme } from '@mui/material/styles';
import type { UploadFile } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';

export type StrapiImageRendererProps = Omit<ImageProps, 'src' | 'alt'> & {
	imageData?:
		| UploadFile
		| {
				backgroundLightImage?: UploadFile;
				backgroundDarkImage?: UploadFile;
		  }
		| null;
	host?: AppContext['host'];
	themeMode?: 'light' | 'dark';
	alt?: string;
	imageFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
	strength?: number;
	sizes?: string;
	src?: string;
};

const StrapiImageRenderer = ({
	host,
	imageData,
	themeMode,
	alt = 'Image',
	imageFit = 'cover',
	strength = 2.0,
	style,
	sizes,
	src,
	...props
}: StrapiImageRendererProps) => {
	// Media Query
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const getActiveImageObject = (): UploadFile | null => {
		if (!imageData) return null;

		if (
			typeof imageData === 'object' &&
			imageData !== null &&
			('backgroundLightImage' in imageData || 'backgroundDarkImage' in imageData)
		) {
			const selectedImage = themeMode === 'dark' ? imageData.backgroundDarkImage : imageData.backgroundLightImage;

			return selectedImage ?? null;
		}

		if (typeof imageData === 'object' && imageData !== null && 'url' in imageData) {
			return imageData as UploadFile;
		}

		return null;
	};

	const getFocalPosition = () => {
		if (imageFit !== 'cover') return 'center';
		// Skip for src
		if (!activeImage) return 'center';

		const rawFocal = activeImage?.focalPoint;
		if (typeof rawFocal !== 'string') return 'center';

		try {
			const { x, y } = JSON.parse(rawFocal);
			const amplify = (val: number) => {
				const center = 50;
				const offset = (val - center) * strength;
				return Math.max(0, Math.min(100, center + offset));
			};
			return `${amplify(x)}% ${amplify(y)}%`;
		} catch {
			return 'center';
		}
	};

	const effectiveHost = src
		? undefined
		: (host ?? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost')); // TODO: Get a better default strategy
	const activeImage = imageData ? getActiveImageObject() : null;

	const imageUrl =
		src ??
		(activeImage
			? getStrapiImageUrl({
					host: effectiveHost || '',
					imageData: activeImage,
					isMobile,
					themeMode
				})
			: null);

	if (!imageUrl) return null;

	return (
		<Image
			sizes={sizes}
			src={imageUrl}
			alt={alt}
			style={{
				objectFit: imageFit,
				objectPosition: getFocalPosition(),
				...style
			}}
			{...props}
		/>
	);
};

export default StrapiImageRenderer;
